import type { AnySection, SectionBase } from '@wix/add-panel-common';
import { MouseEventHandler, MutableRefObject, RefObject, useCallback, useEffect, useRef } from 'react';
import type { CustomScrollRef } from '@wix/wix-base-ui';
import { useUtils } from '../../../UtilsProvider';
import type { CategoryViewRef } from '../../CategoryView.types';
import type { SectionNodeInterval, SectionNodeRect } from './useCategoryViewScroll.types';
import { calculateSectionDimensions, calculateSectionNodeIntervals, getNextSectionIdToSelect } from './utils';

interface UseCategoryViewScrollArgs {
  sections: SectionBase[];
  hasCategorySearch?: boolean;
  onScroll: (sectionId: string, y: number) => void;
}

interface UseCategoryViewScrollBehavior {
  customScrollRef: MutableRefObject<CustomScrollRef | null>;
  contentWrapperRef: RefObject<HTMLDivElement>;
  getScrollTop: () => number;
  isScrollInProcess: () => boolean;
  scrollTo: CategoryViewRef['scrollTo'];
  scrollToSectionTop: CategoryViewRef['scrollToSectionTop'];
  handleScroll: MouseEventHandler;
  registerSectionElement: (sectionId: string, element: HTMLElement, isAsyncSection?: boolean) => void;
}

const SCROLL_DURATION_IN_SECONDS = 0.5;
const SCROLL_DELAY_IN_SECONDS = 0;
export const CATEGORY_SEARCH_BAR_OFFSET = 62;

export const useCategoryViewScroll = ({
  sections,
  onScroll,
  hasCategorySearch,
}: UseCategoryViewScrollArgs): UseCategoryViewScrollBehavior => {
  const { uiAnimations } = useUtils();
  const customScrollRef = useRef<CustomScrollRef>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const intervalsRef = useRef<SectionNodeInterval[]>([]);
  const sectionNodesRef = useRef<Record<string, HTMLElement>>({});
  const sectionRectsRef = useRef<Record<string, SectionNodeRect>>({});
  const isScrollAnimationInProcessRef = useRef<boolean>(false);
  const containerTopPositionRef = useRef<number | null>(null);

  const getScrollTop = useCallback(() => customScrollRef.current?.state.scrollPos || 0, []);

  const isScrollInProcess = useCallback(() => isScrollAnimationInProcessRef.current, []);

  const scrollTo = useCallback<CategoryViewRef['scrollTo']>(
    (args, onComplete = () => undefined) => {
      const innerScroll: CategoryViewRef['scrollTo'] = ({ top, forceScroll }, _onComplete) => {
        const container = customScrollRef.current?.innerContainer;

        if (!container || (isScrollAnimationInProcessRef.current && !forceScroll)) {
          return;
        }

        isScrollAnimationInProcessRef.current = true;

        uiAnimations.scrollTo({ y: top }, SCROLL_DURATION_IN_SECONDS, SCROLL_DELAY_IN_SECONDS, container, () => {
          isScrollAnimationInProcessRef.current = false;

          if (_onComplete) {
            _onComplete(top);
          }
        });
      };

      if (getScrollTop() === args.top) {
        return;
      }

      window.requestAnimationFrame(() => innerScroll(args, onComplete));
    },
    [uiAnimations, getScrollTop],
  );

  const scrollToSectionTop = useCallback(
    (sectionId, onComplete) => {
      if (!sectionRectsRef.current[sectionId]) {
        console.warn(sectionId, 'section is not implemented yet.');
        return;
      }

      const { top } = sectionRectsRef.current[sectionId];

      const newTop = top < 8 ? 0 : Math.max(0, top);

      scrollTo({ top: newTop, forceScroll: true }, onComplete);
    },
    [scrollTo],
  );

  const handleScroll = useCallback(
    (event) => {
      if (isScrollAnimationInProcessRef.current) {
        return;
      }

      const intervals = intervalsRef.current;
      const scrollTop = event.target.scrollTop;

      const scrolledToBottom =
        scrollTop + customScrollRef.current?.innerContainer.offsetHeight === contentWrapperRef.current?.offsetHeight;
      const sectionToOpenId = scrolledToBottom
        ? intervals.slice(-1)[0].id
        : getNextSectionIdToSelect(intervals, scrollTop);

      if (!sectionToOpenId) {
        return;
      }

      onScroll(sectionToOpenId, scrollTop);
    },
    [onScroll],
  );

  const storeSectionRects = useCallback(() => {
    if (!customScrollRef.current?.innerContainer) {
      return;
    }

    const containerScrollTopPosition = customScrollRef.current.innerContainer.scrollTop;
    if (containerTopPositionRef.current === null) {
      containerTopPositionRef.current = customScrollRef.current.innerContainer.getBoundingClientRect().top || 0;
    }

    const searchBarOffset = hasCategorySearch ? CATEGORY_SEARCH_BAR_OFFSET : 0;
    const additionalOffsetTop = containerScrollTopPosition - (containerTopPositionRef.current || 0) - searchBarOffset;

    sectionRectsRef.current = calculateSectionDimensions(sectionNodesRef.current, additionalOffsetTop);
  }, [hasCategorySearch]);

  const saveSectionRectsAndCalculateIntervals = useCallback(() => {
    storeSectionRects();

    intervalsRef.current = calculateSectionNodeIntervals(sections, sectionRectsRef.current);
  }, [storeSectionRects, sections]);

  const registerSectionElement = useCallback(
    (sectionId: string, element: HTMLElement, isAsyncSection = false) => {
      sectionNodesRef.current[sectionId] = element;

      if (isAsyncSection) {
        saveSectionRectsAndCalculateIntervals();
      }
    },
    [saveSectionRectsAndCalculateIntervals],
  );

  useEffect(() => {
    saveSectionRectsAndCalculateIntervals();
  }, [saveSectionRectsAndCalculateIntervals]);

  return {
    getScrollTop,
    isScrollInProcess,
    scrollTo,
    scrollToSectionTop,
    registerSectionElement,
    customScrollRef,
    contentWrapperRef,
    handleScroll,
  };
};
